import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { OutlineCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import Row from 'components/Row'
import { MouseoverTooltip } from 'components/Tooltip'
import { useContext, useMemo } from 'react'
import { AlertTriangle } from 'react-feather'
import styled, { ThemeContext } from 'styled-components'
import { HideSmall, ThemedText } from 'theme'
import { opacify } from 'theme/utils'

const StyledCard = styled(OutlineCard)<{ color: string }>`
  padding: 12px;
  border: 1px solid ${({ color }) => opacify(24, color)};
  border-radius: 4px;
`

interface RedeemWarningProps {
  multiplier?: number
  input?: CurrencyAmount<Token>
  output?: CurrencyAmount<Token>
}

export default function RedeemWarning({ multiplier, input, output }: RedeemWarningProps) {
  const theme = useContext(ThemeContext)
  const percentageString = useMemo(
    () => (multiplier !== undefined ? (100 - multiplier * 100).toFixed(2) : null),
    [multiplier]
  )
  const forfeitAmount = useMemo(() => {
    if (multiplier === undefined || !input || !output) return null
    const forfeit = input.subtract(output)
    return forfeit.greaterThan(0) ? forfeit : null
  }, [input, output, multiplier])

  const color = useMemo(() => {
    if (multiplier === undefined) return theme.deprecated_error
    if (multiplier < 0.8) return theme.deprecated_error
    return theme.deprecated_warning
  }, [theme, multiplier])
  if (percentageString === null) return null

  return (
    <StyledCard color={color}>
      <AutoColumn gap="16px">
        <MouseoverTooltip
          text={
            <>You will not receive the full refund amount because you are going to complete the expedited procedure</>
          }
        >
          <Row gap="6px" align="center">
            <ThemedText.DeprecatedSubHeader color={color}>
              <HideSmall>
                <AlertTriangle size="18px" style={{ marginTop: '2px' }} />
              </HideSmall>
            </ThemedText.DeprecatedSubHeader>
            <ThemedText.DeprecatedSubHeader color={color}>
              <span style={{ fontWeight: 700 }}>{forfeitAmount?.toSignificant(2)}</span> KDK will be forfeited by
              choosing shorter duration
            </ThemedText.DeprecatedSubHeader>
          </Row>
        </MouseoverTooltip>
      </AutoColumn>
    </StyledCard>
  )
}
